import React, { useState } from "react";
import {
  Formik,
  FormikActions,
  Form as FormikForm,
  Field,
  FormikProps,
} from "formik";
import { injectIntl, FormattedMessage } from "react-intl";
import TextInput from "../../text-input";
import Typography from "../../typography";
import InputErrorMessage from "../../input-error-message";

import messages from "../PageNewPlayer.intl";
import { styled } from "@material-ui/styles";
import { IStyledArguments } from "../../../types/styled-arguments";
import RegisterStepButton from "../register-step-button";
import { required } from "../../form-utils";
import { POSITION_LABELS } from "../../../utils/positions";

import Select from "../../select";
import Option from "../../select/option";
import { SelectProps } from "@material-ui/core/Select";
import {
  MuiPickersUtilsProvider,
  KeyboardDatePicker,
} from "@material-ui/pickers";

import DateFnsUtils from "@date-io/date-fns";
import moment from "moment";
import PersonAddIcon from "@material-ui/icons/PersonAdd";
import AccountCircleIcon from "@material-ui/icons/AccountCircle";
import SportsIcon from "@material-ui/icons/Sports";
import EventIcon from "@material-ui/icons/Event";

const Title = styled(({ children }) => (
  <Typography.H5>{children}</Typography.H5>
))((props: IStyledArguments) => {
  const { md } = props.theme.breakpoints.values;

  return {
    color: props.theme.palette.primary.main,
    fontWeight: 700,
    marginBottom: 24,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "12px",
    [`@media (max-width: ${md}px)`]: {
      fontSize: "1.25rem",
      marginBottom: 20,
    },
  };
});

const FormSection = styled("div")(({ theme }: IStyledArguments) => ({
  background: theme.palette.background.paper,
  borderRadius: "16px",
  padding: theme.spacing(3),
  marginBottom: theme.spacing(3),
  border: `1px solid ${theme.palette.extra.borderColor}`,
  [theme.breakpoints.down("sm")]: {
    padding: theme.spacing(2),
    borderRadius: "12px",
  },
}));

const SectionTitle = styled("div")(({ theme }: IStyledArguments) => ({
  fontSize: "1.1rem",
  fontWeight: 600,
  color: theme.palette.primary.main,
  marginBottom: theme.spacing(2),
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(1),
  [theme.breakpoints.down("sm")]: {
    fontSize: "1rem",
  },
}));

const PositionSelect = injectIntl<SelectProps>(({ onChange, value, intl }) => {
  return (
    <Select
      id="position-select"
      label={intl.formatMessage(messages.position)}
      value={value}
      onChange={onChange}
      fullWidth
    >
      {Object.keys(POSITION_LABELS).map((key, idx) => (
        <Option key={idx} value={key}>
          {POSITION_LABELS[key]}
        </Option>
      ))}
    </Select>
  );
});

export interface IFormValues {
  // email: string;
  name: string;
  surname: string;
  nickname?: string;
  number?: number;
  position: string;
  dateOfBirth: string;
}

interface IForm {
  onSubmit: (arg: IFormValues) => void;
  checkValid: boolean;
  // email: string;
  name: string;
  surname: string;
  nickname?: string;
  number?: number;
  position: string;
  dateOfBirth: string;
}

const StyledForm = styled(FormikForm)(() => ({
  display: "flex",
  flexDirection: "column",
}));

const NewPlayerForm = injectIntl<IForm>(
  ({
    onSubmit,
    checkValid, // email,
    name,
    surname,
    number,
    position,
    nickname, // dateOfBirth,
    intl,
  }) => {
    const [positionOption, setPositionOption] = useState<string>("");
    const [selectedDate, setSelectedDate] = useState<Date>(new Date());

    const handleDateChange = (date: Date) => {
      console.log("date================");
      console.log(date);
      setSelectedDate(date);
    };
    return (
      <Formik
        initialValues={{
          // email,
          name,
          surname,
          number,
          position,
          nickname,
          dateOfBirth: `${moment(selectedDate).format("YYYY-MM-DD")}`,
        }}
        isInitialValid={checkValid}
        onSubmit={(
          values: IFormValues,
          actions: FormikActions<IFormValues>
        ) => {
          onSubmit(values);
          actions.setSubmitting(false);
        }}
        render={({
          errors,
          touched,
          // values,
          isValid,
          setFieldValue,
        }: FormikProps<IFormValues>) => (
          <>
            <Title>
              <PersonAddIcon style={{ fontSize: "32px" }} />
              <FormattedMessage {...messages.registerTitle} />
            </Title>
            <StyledForm data-testid="add-player-form">
              {/* Personal Information Section */}
              <FormSection>
                <SectionTitle>
                  <AccountCircleIcon />
                  Personal Information
                </SectionTitle>
                <Field
                  name="name"
                  validate={required(intl)}
                  error={errors.name && touched.name}
                  component={TextInput}
                  inputProps={{
                    "data-testid": "name",
                  }}
                  label={intl.formatMessage(messages.name)}
                />
                <InputErrorMessage name="name" />
                <Field
                  name="surname"
                  validate={required(intl)}
                  error={errors.surname && touched.surname}
                  component={TextInput}
                  inputProps={{
                    "data-testid": "surname",
                  }}
                  label={intl.formatMessage(messages.surname)}
                />
                <InputErrorMessage name="surname" />
              </FormSection>

              {/* Player Details Section */}
              <FormSection>
                <SectionTitle>
                  <SportsIcon />
                  Player Details
                </SectionTitle>
                <PositionSelect
                  value={positionOption}
                  onChange={(event: any) => {
                    setPositionOption(event.target.value);
                    setFieldValue("position", event.target.value);
                  }}
                />
              </FormSection>

              {/* Date of Birth Section */}
              <FormSection>
                <SectionTitle>
                  <EventIcon />
                  Date of Birth
                </SectionTitle>
                <MuiPickersUtilsProvider utils={DateFnsUtils}>
                  <KeyboardDatePicker
                    margin="normal"
                    id="date-picker-dialog"
                    label={intl.formatMessage(messages.dateOfBirth)}
                    format="yyyy-MM-dd"
                    value={selectedDate}
                    onChange={(date: any) => {
                      handleDateChange(date);
                      setFieldValue(
                        "dateOfBirth",
                        moment(date).format("YYYY-MM-DD")
                      );
                    }}
                    KeyboardButtonProps={{
                      "aria-label": "change date",
                    }}
                    fullWidth
                  />
                </MuiPickersUtilsProvider>
              </FormSection>

              <div>
                <RegisterStepButton
                  type="submit"
                  data-testid="go-next-button"
                  disabled={!isValid}
                >
                  {intl.formatMessage(messages.next)}
                </RegisterStepButton>
              </div>
            </StyledForm>
          </>
        )}
      />
    );
  }
);

export default NewPlayerForm;
